
import { defineComponent, PropType } from 'vue'
import { mapState } from 'vuex'

import InstallAutofixMixin from '~/mixins/installAutofixMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'

import { toSentenceCase } from '@/utils/string'

import {
  AnalyzerMetaInterface,
  AnalyzerMetaProperitiesInterface,
  TransformerInterface
} from '~/store/analyzer/list'
import { AnalyzerAnalyzerType, Repository, TransformerTool } from '~/types/types'

export default defineComponent({
  name: 'Analyzer',
  mixins: [InstallAutofixMixin, RoleAccessMixin],
  props: {
    name: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    analyzerMeta: {
      type: Object as PropType<AnalyzerMetaInterface>,
      default: () => ({})
    },
    analyzerLogo: {
      type: String,
      default: ''
    },
    analyzerType: {
      type: String,
      default: ''
    },
    selectedAnalyzer: {
      type: Object as PropType<{
        name: string
        enabled: boolean
        meta: Record<string, unknown>
      }>,
      default: () => ({ name: '', enabled: false, meta: {} })
    },
    availableTransformers: {
      type: Array as PropType<Array<TransformerTool>>,
      default: () => []
    },
    selectedTransformers: {
      type: Array as PropType<Array<TransformerInterface>>,
      default: () => []
    },
    forTemplate: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    disableActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      configItems: [] as Array<AnalyzerMetaProperitiesInterface>,
      transformerItems: [] as Array<TransformerInterface>,
      invalidFields: [] as Array<string>,
      isCollapsed: false,
      AnalyzerAnalyzerType
    }
  },
  mounted() {
    this.generateConfigItems()
    this.generateTransformerItems()
  },
  watch: {
    readOnly: {
      handler: 'updateIsCollapseOnReadStatusChange'
    },
    'selectedTransformers.length': {
      handler: 'updateTransformerItems'
    },
    configItems: {
      deep: true,
      handler: 'analyzersUpdated'
    },
    transformerItems: {
      deep: true,
      handler: 'transformersUpdated'
    }
  },
  computed: {
    ...mapState('repository/detail', {
      repository: (state: any) => state.repository as Repository
    }),
    hasCollapsibleContent(): boolean {
      return this.configItems.length > 0 || this.showTransformers || this.showInstallAutofixAppCTA
    },
    properties(): AnalyzerMetaInterface['properties'] {
      return this.analyzerMeta.properties
    },
    showInstallAutofixAppCTA(): boolean {
      // Show the CTA to install Autofix app only on config generator page
      if (this.$route.name !== 'provider-owner-repo-generate-config') {
        return false
      }

      return (
        this.availableTransformers.length > 0 &&
        this.allowAutofix &&
        !this.repository.isAutofixEnabled
      )
    },
    showTransformers(): boolean {
      if (this.forTemplate) {
        return this.availableTransformers.length > 0
      }

      return Boolean(this.repository.isAutofixEnabled && this.availableTransformers.length > 0)
    }
  },
  methods: {
    toSentenceCase,
    /**
     * Update collapse state based on the value for readOnly prop
     *
     * @returns {void}
     */
    updateIsCollapseOnReadStatusChange(): void {
      if (this.readOnly) {
        this.isCollapsed = false
      }
    },
    /**
     * Toggle transformer enabled state based on the value for readOnly prop
     *
     * @returns {void}
     */
    toggleTransformer(transformer: TransformerInterface) {
      if (this.readOnly || this.disableActions) {
        return
      }

      transformer.enabled = !transformer.enabled
    },
    /**
     * Discards Code Formatters and emit `onClose` event
     *
     * @returns {void}
     */
    onClose(): void {
      // remove all transformers
      this.removeAllTransformers()
      this.$emit('onClose')
    },
    /**
     * Replace template with actual/placeholder data
     *
     * @param {string} candidate
     * @returns {string}
     */
    parseTemplate(candidate: string): string {
      const hostmap: Record<string, string> = {
        gh: 'github.com',
        gl: 'gitlab.com',
        bb: 'bitbucket.com'
      }

      const { owner, repo, provider } = this.$route.params
      let host = provider in hostmap ? hostmap[provider] : ''

      if (this.repository.vcsHost) {
        host = this.repository.vcsHost
      }
      return candidate
        .replaceAll('<%=  vcs_host %>', host)
        .replaceAll('<%=  login %>', owner)
        .replaceAll('<%=  name %>', this.forTemplate ? '<repository-name>' : repo)
    },
    /**
     * Check if the config selected property has template data
     *
     * @param {AnalyzerMetaProperitiesInterface} config
     * @returns {boolean}
     */
    hasTemplate(config: AnalyzerMetaProperitiesInterface): boolean {
      if (typeof config.selected === 'string') {
        return (
          config.selected.includes('<%=  vcs_host %>') ||
          config.selected.includes('<%=  login %>') ||
          config.selected.includes('<%=  name %>')
        )
      }
      return false
    },
    /**
     * Generate config items from analyzer meta properties
     *
     * @returns {void}
     */
    generateConfigItems(): void {
      if (this.analyzerMeta?.required || this.analyzerMeta?.optional_required) {
        let props: Array<string> = []

        if (this.analyzerMeta?.required?.length) {
          props = props.concat(this.analyzerMeta.required)
        }

        if (this.analyzerMeta?.optional_required?.length) {
          props = props.concat(this.analyzerMeta.optional_required)
        }

        props = [...new Set(props)]

        this.configItems = props
          .filter((name) => name in this.analyzerMeta.properties)
          .map((name) => {
            const config = this.analyzerMeta.properties[name]
            // Object.assign(config, this.selectedAnalyzer)
            let selected
            if (this.selectedAnalyzer.enabled && this.selectedAnalyzer?.meta?.[name]) {
              selected = this.selectedAnalyzer.meta[name]

              if (typeof selected === 'string') {
                selected = this.parseTemplate(selected)
              }

              if (Array.isArray(selected) && selected.length) {
                selected = selected.map((item) =>
                  typeof item === 'string' ? this.parseTemplate(item) : item
                )
              }
            }

            if (config.type === 'boolean') {
              config.default = Boolean(config.default)
            }

            if (config.type === 'array' && Array.isArray(config.default)) {
              config.default = config.default.map((item) => {
                return typeof item === 'string' ? this.parseTemplate(item) : item
              })
            }

            if (config.type === 'string') {
              if (typeof config.default === 'string') {
                config.default = this.parseTemplate(config.default)
              }
              if (Array.isArray(config.default) && config.default.length) {
                config.default = this.parseTemplate(config.default[0])
              }
            }

            return {
              ...config,
              name,
              selected: selected ? selected : config.default
            } as AnalyzerMetaProperitiesInterface
          })
      } else {
        this.configItems = []
      }
    },
    /**
     * Generate Transformer items based on the selection
     *
     * @returns {void}
     */
    generateTransformerItems(): void {
      const selected = this.selectedTransformers
        .filter(({ enabled }) => enabled)
        .map(({ name }) => name)

      this.transformerItems = this.availableTransformers.map((transformer) => {
        return {
          ...transformer,
          enabled: selected.includes(transformer.shortcode)
        }
      })
    },
    /**
     * Update Code Formatter items when selectedTransformers list changes
     *
     * @returns {void}
     */
    updateTransformerItems(): void {
      this.generateTransformerItems()
    },
    /**
     * Update the value for selected key based on the value for isChecked
     *
     * @param {string} option
     * @param {number} isChcked
     * @param {Record<string, string[]>} objectToUpdate
     * @returns {void}
     */
    updateChecks(
      option: string,
      isChecked: number,
      objectToUpdate: AnalyzerMetaProperitiesInterface
    ): void {
      if (!objectToUpdate.selected) {
        objectToUpdate.selected = []
      }
      if (!Array.isArray(objectToUpdate.selected)) {
        return
      }
      if (isChecked) {
        objectToUpdate.selected.push(option)
      } else {
        objectToUpdate.selected = objectToUpdate.selected.filter(
          (candidate) => candidate !== option
        )
      }
    },
    /**
     * Update the value for selected key based on the value for isChecked
     *
     * @param {string} option
     * @param {Record<string, string[]>} objectToUpdate
     * @returns {void}
     */
    updateArray(option: string, objectToUpdate: AnalyzerMetaProperitiesInterface): void {
      objectToUpdate.selected = option
        .split(',') // comma separeted
        .filter((val) => val) // non empty
        .map((val) => val.trim()) // no trailing or leading spaces
    },
    analyzersUpdated(): void {
      const meta: Record<string, string | number | boolean | string[]> = {}
      this.configItems.forEach((configItem: AnalyzerMetaProperitiesInterface) => {
        if (configItem.selected) {
          meta[configItem.name] = configItem.selected
        }
      })

      this.$emit('analyzersUpdated', {
        name: this.name,
        meta,
        enabled: true
      })
    },
    validateConfig(collapseCardsIfRequired = true): number {
      const requiredFields = this.analyzerMeta.required || []
      this.invalidFields = this.configItems
        .filter((configItem: AnalyzerMetaProperitiesInterface) => {
          return requiredFields.includes(configItem.name) && !configItem.selected
        })
        .map((configItem: AnalyzerMetaProperitiesInterface) => configItem.name)

      if (collapseCardsIfRequired) {
        this.isCollapsed = this.invalidFields.length === 0
      }

      return this.invalidFields.length
    },
    transformersUpdated(): void {
      const transformers: Record<string, Record<string, string | boolean>> = {}
      this.transformerItems.forEach((transformer) => {
        transformers[transformer.shortcode] = {
          name: transformer.shortcode,
          enabled: transformer.enabled
        }
      })

      this.$emit('transformersUpdated', transformers)
    },
    removeAllTransformers(): void {
      this.transformerItems.forEach((transformer) => {
        transformer.enabled = false
      })
      this.transformersUpdated()
    },
    toggleCollapsible(): void {
      this.isCollapsed = !this.isCollapsed
    }
  }
})
